import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ActivityIcon } from 'lucide-react';
import type { UserActivity } from '@/types/database.types';
import Pagination from '@/components/Pagination';

interface ActivityListProps {
  activities: UserActivity[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function ActivityList({ activities, currentPage, totalPages, onPageChange }: ActivityListProps) {
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Activity History</h2>
      </div>
      <div className="divide-y divide-gray-200">
        {activities.map((activity) => (
          <div key={activity.id} className="px-6 py-4 hover:bg-gray-50">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ActivityIcon className="h-5 w-5 text-gray-600" />
              </div>
              <div className="ml-4 flex-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">
                    {activity.title}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                  </p>
                </div>
                {activity.content && (
                  <p className="mt-1 text-sm text-gray-600">{activity.content}</p>
                )}
                <div className="mt-2">
                  <Link
                    to={`/snap/${activity.snap_id}`}
                    className="text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    View SNAP →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
        {activities.length === 0 && (
          <div className="px-6 py-8 text-center text-gray-500">
            <ActivityIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No activity yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start creating SNAPs, voting, and commenting to see your activity here.
            </p>
          </div>
        )}
      </div>
      <div className="px-6 py-4 border-t border-gray-200">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}